import Alert from '@/components/Alert';
import Button from '@/components/Button';
import { AuthRecoveryShell } from '@/components/Forms/AuthRecoveryShell';
import { EmailField } from '@/components/Forms/fields';
import { Routes } from '@/lib/constants';
import getQueryClientConfig from '@/lib/react-query/queryClientConfig';
import { useForgotPassword } from '@/lib/react-query/auth/useForgotPassword';
import { getBrandContext } from '@lib-server/brand/getBrandContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { userForgotPasswordSchema } from 'lib-server/validation';
import type { GetServerSidePropsContext } from 'next';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { UserForgotPasswordData } from 'types/models/User';
const ForgotPassword = () => {
  const [queryClient] = useState(() => new QueryClient(getQueryClientConfig()));

  return (
    <QueryClientProvider client={queryClient}>
      <ForgotPasswordContent />
    </QueryClientProvider>
  );
};

const ForgotPasswordContent = () => {
  const { register, handleSubmit, formState, getValues } =
    useForm<UserForgotPasswordData>({
      resolver: zodResolver(userForgotPasswordSchema),
    });

  const {
    mutate: forgotPassword,
    isPending: isLoading,
    isError,
    error,
    isSuccess,
  } = useForgotPassword();

  const { errors } = formState;

  const onSubmitRegisterPassword = ({ email }: UserForgotPasswordData) => {
    forgotPassword(email);
  };

  const Success = () => {
    return (
      <div className="space-y-6 text-sm leading-normal text-black">
        <p className="">
          If an account exists for {getValues('email')}, password reset
          instructions will be sent there.
        </p>
        <p className="">
          {`If you don’t receive an email soon, please check your spam folder.`}
        </p>
        {error && <p className="text-center text-red-600">{error.message}</p>}
        <div className="w-full justify-center text-center">
          <Button
            type="button"
            className="w-full"
            href={Routes.SITE.LOGIN}
            color="minimal"
            role="button"
          >
            Back to Sign in
          </Button>
        </div>
      </div>
    );
  };
  return (
    <AuthRecoveryShell
      title="Forgot Password?"
      description="Reset your password"
    >
      {isError && (
        <Alert
          variant="error"
          message={(error as any).response?.data?.message || error.message}
        />
      )}
      {isSuccess ? (
        <Success />
      ) : (
        <form
          className="flex flex-col"
          onSubmit={handleSubmit(onSubmitRegisterPassword)}
        >
          <EmailField
            {...register('email')}
            label="Email Address"
            type="email"
            required
            placeholder="Enter your email address"
          />
          {errors.email && (
            <Alert variant="error" message={errors.email.message} />
          )}
          <div className="flex flex-col items-center justify-center pt-5 lg:flex-row">
            <Button type="submit" loading={isLoading} className="w-full">
              Reset Password
            </Button>

            <Button href={Routes.SITE.LOGIN} color="minimal" role="button">
              Sign in
            </Button>
          </div>
        </form>
      )}
    </AuthRecoveryShell>
  );
};
export default ForgotPassword;

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => ({
  props: {
    brand: getBrandContext(context.req),
  },
});
